import { notify } from "@/components/common/Notification";
import { getEnvTenantId, normalizeTenantId } from "@/lib/tenantId";
import {
  getAccessToken,
  getRefreshToken,
  getTenantId,
} from "@/lib/storeManager";
import {
  clearSdAuthSession,
  redirectToSdLogin,
  tryRefreshAccessToken,
} from "@sd/lib/sdAuthSession";
import { hidenMessageAPI } from "@/utils/constant/hidenMessageAPI";
import axios from "axios";

declare module "axios" {
  interface AxiosRequestConfig {
    /** If true, error interceptor will not show global toast (optional / best-effort calls). */
    skipErrorNotification?: boolean;
  }
}

export const axiosConfig = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
});

const ENV_TENANT_ID = getEnvTenantId();

axiosConfig.interceptors.request.use(config => {
  const token = getAccessToken();
  // env variable takes priority over localStorage
  const tenantId = ENV_TENANT_ID || normalizeTenantId(getTenantId());

  // Don't add Authorization header for refresh token requests
  if (
    token &&
    config.headers &&
    !config.url?.includes("/auth/token-refresh/")
  ) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (tenantId && config.headers) {
    config.headers["X-Tenant-Id"] = tenantId;
  }

  if (config.headers) {
    config.headers["X-User-Timezone"] =
      Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  return config;
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosConfig.interceptors.response.use(
  response => {
    const apiStatus = response.status;
    if (
      hidenMessageAPI.some(path => (response.config.url ?? "").includes(path))
    ) {
      return response;
    }
    // if (
    //   (apiStatus === 200 || apiStatus === 201 || apiStatus === 204) &&
    //   response.data?.success
    // ) {
    //   const message = response.data?.success?.message ?? response.data.message;
    //   if (message) {
    //     notify.success(message);
    //   }
    // }
    return response;
  },
  async error => {
    const originalRequest = error.config;
    const skipToast = Boolean(originalRequest?.skipErrorNotification);

    if (
      error.response?.status === 400 ||
      error.response?.status === 404 ||
      error.response?.status === 417
    ) {
      if (!skipToast) {
        notify.error(
          error.response.data.message ??
            error.response.data.error?.message ??
            error.response.data.error
        );
      }
      return Promise.reject(error);
    }
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      getRefreshToken()
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return axiosConfig(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshed = await tryRefreshAccessToken();
        if (!refreshed) {
          throw new Error("Token refresh failed");
        }
        const newAccessToken = getAccessToken();
        if (!newAccessToken) {
          throw new Error("Token refresh returned no access token");
        }
        axiosConfig.defaults.headers.common["Authorization"] =
          `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axiosConfig(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearSdAuthSession();
        redirectToSdLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else if (error.response?.status === 401) {
      clearSdAuthSession();
      redirectToSdLogin();
      return Promise.reject(error);
    } else {
      if (!skipToast) {
        const data = error.response?.data as
          | {
              error?: { message?: string } | string;
              message?: string;
            }
          | undefined;
        const nestedError = data?.error;
        notify.error(
          (typeof nestedError === "object" ? nestedError?.message : nestedError) ??
            data?.message ??
            error.message ??
            "Request failed"
        );
      }
      return Promise.reject(error);
    }
  }
);
